import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  RefreshCw,
  Shield,
  AlertCircle,
  CheckCircle,
  Clock,
  Mail,
  Wallet,
  Trophy,
  Calendar,
  XCircle,
  HelpCircle,
  ChevronDown,
  ChevronUp,
  FileText,
  AlertTriangle,
  ThumbsUp
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Link } from 'react-router-dom';

const Refund = () => {
  const [expandedSections, setExpandedSections] = useState<string[]>([]);

  const toggleSection = (section: string) => {
    if (expandedSections.includes(section)) {
      setExpandedSections(expandedSections.filter(s => s !== section));
    } else {
      setExpandedSections([...expandedSections, section]);
    }
  };

  const quickStats = [
    { value: '24-48h', label: 'Refund Processing', icon: Clock },
    { value: '100%', label: 'Secure Transactions', icon: Shield },
    { value: '24/7', label: 'Support Available', icon: HelpCircle },
    { value: '5-7d', label: 'Bank Processing', icon: Calendar },
  ];

  const refundScenarios = [
    {
      id: 'cancellation',
      title: 'Tournament Cancellation',
      icon: <Trophy className="w-6 h-6 text-[#00fff5]" />,
      scenarios: [
        'Tournament cancelled by TheArenaX due to technical issues',
        'Insufficient players for tournament to proceed',
        'Tournament rescheduled and you cannot attend new time',
      ],
      resolution: 'Full refund of entry coins to your wallet within 24-48 hours',
      color: '#00fff5'
    },
    {
      id: 'failed',
      title: 'Failed Transactions',
      icon: <AlertCircle className="w-6 h-6 text-[#F59E0B]" />,
      scenarios: [
        'Payment succeeded but coins not credited',
        'Duplicate payment processed',
        'Incorrect amount deducted',
      ],
      resolution: 'Refund to original payment method within 5-7 business days after verification',
      color: '#F59E0B'
    },
    {
      id: 'withdrawal',
      title: 'Failed Withdrawals',
      icon: <Wallet className="w-6 h-6 text-[#EF4444]" />,
      scenarios: [
        'Incorrect UPI ID provided',
        'Bank account issues',
        'Technical error during transfer',
      ],
      resolution: 'Amount credited back to wallet within 3-5 business days',
      color: '#EF4444'
    }
  ];

  const noRefundScenarios = [
    {
      title: 'Tournament Already Started',
      desc: 'No refunds once tournament has begun, regardless of participation'
    },
    {
      title: 'Personal Technical Issues',
      desc: 'Internet problems, device issues, or personal emergencies'
    },
    {
      title: 'Change of Mind',
      desc: 'Deciding not to play after joining'
    },
    {
      title: 'Rule Violations',
      desc: 'Disqualification due to cheating or breaking fair play rules'
    },
    {
      title: 'Forgot to Join',
      desc: 'Missing tournament start time'
    },
    {
      title: 'Dissatisfaction with Performance',
      desc: 'Not happy with match results or personal gameplay'
    }
  ];

  const process = [
    {
      step: '01',
      title: 'Submit Request',
      desc: 'Email support@lastzoneindia.com with your transaction details'
    },
    {
      step: '02',
      title: 'Verification',
      desc: 'Our team verifies the issue within 24 hours'
    },
    {
      step: '03',
      title: 'Approval',
      desc: 'If eligible, refund is approved'
    },
    {
      step: '04',
      title: 'Processing',
      desc: 'Refund processed to wallet or payment method'
    }
  ];

  const faqs = [
    {
      q: 'How long do refunds take?',
      a: 'Wallet refunds: 24-48 hours. Bank/Payment method refunds: 5-7 business days depending on your bank.'
    },
    {
      q: 'Can I cancel my withdrawal request?',
      a: 'No, withdrawal requests cannot be cancelled once submitted. If withdrawal fails, amount is auto-credited back to your wallet.'
    },
    {
      q: 'What if I entered wrong UPI ID?',
      a: 'The withdrawal will fail and amount will be returned to your wallet within 3-5 business days. You can then request again with correct UPI ID.'
    },
    {
      q: 'Do you refund coin purchases?',
      a: 'Coin purchases are generally non-refundable. Exceptions only for technical failures where coins were not credited.'
    },
    {
      q: 'What about tournament entry fees?',
      a: 'Entry fees are refunded only if WE cancel the tournament. No refunds for player cancellations after tournament starts.'
    }
  ];

  return (
    <div className="min-h-screen bg-[#0A0F1E]">
      <Navbar />
      
      {/* Hero Section with Animated Background */}
      <section className="relative pt-32 pb-20 px-4 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-96 h-96 bg-[#00fff5] rounded-full filter blur-3xl opacity-10"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#7000ff] rounded-full filter blur-3xl opacity-10"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] border border-[#00fff5] rounded-full opacity-5 animate-pulse"></div>
        </div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <div className="inline-block p-4 bg-gradient-to-r from-[#00fff5] to-[#7000ff] rounded-2xl mb-6">
              <RefreshCw className="w-12 h-12 text-white" />
            </div>
            <h1 className="text-5xl md:text-7xl font-rajdhani font-bold text-white mb-6">
              Refund & <span className="text-[#00fff5]">Cancellation</span>
            </h1>
            <p className="text-xl text-gray-400 font-rajdhani max-w-3xl mx-auto mb-8">
              Clear and transparent policy on refunds, cancellations, and dispute resolution. 
              We're committed to fair treatment for all players.
            </p>
            
            <div className="flex flex-wrap justify-center gap-4">
              <div className="bg-[#1E2338] border border-[#2A2F45] rounded-full px-6 py-2">
                <span className="text-[#00fff5] font-rajdhani font-bold text-sm">Last Updated: March 2026</span>
              </div>
              <div className="bg-[#1E2338] border border-[#2A2F45] rounded-full px-6 py-2">
                <span className="text-[#00fff5] font-rajdhani font-bold text-sm">Version 2.0</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Quick Stats */}
      <section className="py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {quickStats.map((stat, index) => {
              const IconComponent = stat.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-gradient-to-b from-[#1E2338] to-[#14182B] border border-[#2A2F45] rounded-2xl p-6 text-center group hover:border-[#00fff5] transition"
                >
                  <div className="inline-block p-3 bg-[#00fff510] rounded-xl mb-3 group-hover:bg-[#00fff520] transition">
                    <IconComponent className="w-6 h-6 text-[#00fff5]" />
                  </div>
                  <div className="text-2xl md:text-3xl font-rajdhani font-bold text-white mb-1">{stat.value}</div>
                  <div className="text-sm text-gray-400 font-rajdhani">{stat.label}</div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Important Notice */}
      <section className="py-8 px-4">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="bg-gradient-to-r from-[#F59E0B20] to-[#EF444420] border border-[#F59E0B] rounded-2xl p-8"
          >
            <div className="flex items-start gap-4">
              <AlertTriangle className="w-8 h-8 text-[#F59E0B] flex-shrink-0 mt-1" />
              <div>
                <h2 className="text-2xl font-rajdhani font-bold text-white mb-2">Important Notice</h2>
                <p className="text-gray-300 font-rajdhani">
                  Coin purchases are <span className="text-[#F59E0B] font-bold">non-refundable</span> except in cases of technical failure. 
                  Tournament entry fees are only refunded if <span className="text-[#F59E0B] font-bold">TheArenaX cancels</span> the tournament. 
                  Please read the full policy below for all scenarios.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Refund Scenarios */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-rajdhani font-bold text-white mb-4">
              When You <span className="text-[#00fff5]">Get Refunds</span>
            </h2>
            <p className="text-xl text-gray-400 font-rajdhani max-w-3xl mx-auto">
              Eligible scenarios for refunds and resolution
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {refundScenarios.map((scenario, index) => (
              <motion.div
                key={scenario.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-gradient-to-b from-[#1E2338] to-[#14182B] border border-[#2A2F45] rounded-2xl overflow-hidden group hover:border-[#00fff5] transition"
              >
                <div className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-3 bg-[#00fff510] rounded-xl group-hover:bg-[#00fff520] transition">
                      {scenario.icon}
                    </div>
                    <h3 className="text-xl font-rajdhani font-bold text-white">{scenario.title}</h3>
                  </div>

                  <ul className="space-y-3 mb-6">
                    {scenario.scenarios.map((item, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-[#00E676] flex-shrink-0 mt-0.5" />
                        <span className="text-gray-400 text-sm font-rajdhani">{item}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="bg-[#1E2338] border border-[#2A2F45] rounded-xl p-4">
                    <p className="text-sm font-rajdhani" style={{ color: scenario.color }}>
                      <span className="font-bold">Resolution:</span> {scenario.resolution}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* No Refund Scenarios */}
      <section className="py-20 px-4 bg-[#0A0F1E]">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-block px-4 py-2 bg-[#EF444420] border border-[#EF4444] rounded-full mb-6">
                <span className="text-[#EF4444] font-rajdhani font-bold text-sm">NO REFUNDS</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-rajdhani font-bold text-white mb-6">
                When Refunds Are <span className="text-[#EF4444]">Not Provided</span>
              </h2>
              <p className="text-gray-400 font-rajdhani text-lg mb-8">
                Please understand that we cannot provide refunds in these situations:
              </p>

              <div className="space-y-4">
                {noRefundScenarios.map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.05 }}
                    className="bg-[#1E2338] border border-[#2A2F45] rounded-xl p-4"
                  >
                    <div className="flex items-start gap-3">
                      <XCircle className="w-5 h-5 text-[#EF4444] flex-shrink-0 mt-0.5" />
                      <div>
                        <h3 className="text-white font-rajdhani font-bold mb-1">{item.title}</h3>
                        <p className="text-gray-400 text-sm">{item.desc}</p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="bg-gradient-to-b from-[#1E2338] to-[#14182B] border border-[#2A2F45] rounded-2xl p-8"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 bg-[#00fff510] rounded-xl">
                  <FileText className="w-8 h-8 text-[#00fff5]" />
                </div>
                <h3 className="text-2xl font-rajdhani font-bold text-white">Policy Summary</h3>
              </div>

              <div className="space-y-4">
                <div className="flex justify-between items-center pb-2 border-b border-[#2A2F45]">
                  <span className="text-gray-400">Tournament Entry</span>
                  <span className="text-[#F59E0B] font-rajdhani font-bold">No Refunds*</span>
                </div>
                <div className="flex justify-between items-center pb-2 border-b border-[#2A2F45]">
                  <span className="text-gray-400">Coin Purchases</span>
                  <span className="text-[#F59E0B] font-rajdhani font-bold">Non-Refundable</span>
                </div>
                <div className="flex justify-between items-center pb-2 border-b border-[#2A2F45]">
                  <span className="text-gray-400">Failed Transactions</span>
                  <span className="text-[#00E676] font-rajdhani font-bold">Refund Eligible</span>
                </div>
                <div className="flex justify-between items-center pb-2 border-b border-[#2A2F45]">
                  <span className="text-gray-400">Tournament Cancelled by Us</span>
                  <span className="text-[#00E676] font-rajdhani font-bold">Full Refund</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Duplicate Payments</span>
                  <span className="text-[#00E676] font-rajdhani font-bold">Refund Eligible</span>
                </div>
              </div>

              <p className="text-gray-500 text-sm mt-6 italic">
                *Except when tournament is cancelled by TheArenaX
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Refund Process */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <div className="inline-block p-3 bg-[#00fff510] rounded-xl mb-4">
              <RefreshCw className="w-8 h-8 text-[#00fff5]" />
            </div>
            <h2 className="text-4xl md:text-5xl font-rajdhani font-bold text-white mb-4">
              Refund <span className="text-[#00fff5]">Process</span>
            </h2>
            <p className="text-xl text-gray-400 font-rajdhani max-w-3xl mx-auto">
              Simple steps to request and receive your refund
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {process.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="relative"
              >
                <div className="bg-gradient-to-b from-[#1E2338] to-[#14182B] border border-[#2A2F45] rounded-2xl p-6 text-center group hover:border-[#00fff5] transition">
                  <div className="w-12 h-12 bg-gradient-to-r from-[#00fff5] to-[#7000ff] rounded-xl flex items-center justify-center font-rajdhani font-bold text-white text-xl mx-auto mb-4">
                    {step.step}
                  </div>
                  <h3 className="text-white font-rajdhani font-bold mb-2">{step.title}</h3>
                  <p className="text-gray-400 text-sm">{step.desc}</p>
                </div>
                {index < process.length - 1 && (
                  <div className="hidden md:block absolute top-1/2 -right-3 transform -translate-y-1/2 text-[#00fff5]">
                    →
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-4 bg-[#0A0F1E]">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-rajdhani font-bold text-white mb-4">
              Frequently Asked <span className="text-[#00fff5]">Questions</span>
            </h2>
          </motion.div>

          <div className="space-y-4">
            {faqs.map((faq, index) => {
              const sectionId = `faq-${index}`;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-[#1E2338] border border-[#2A2F45] rounded-xl overflow-hidden hover:border-[#00fff5] transition"
                >
                  <button
                    onClick={() => toggleSection(sectionId)}
                    className="w-full p-6 flex items-center justify-between text-left hover:bg-[#2A2F45] transition"
                  >
                    <span className="text-white font-rajdhani font-bold">{faq.q}</span>
                    {expandedSections.includes(sectionId) ? (
                      <ChevronUp className="w-5 h-5 text-[#00fff5] flex-shrink-0 ml-4" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0 ml-4" />
                    )}
                  </button>
                  
                  {expandedSections.includes(sectionId) && (
                    <div className="px-6 pb-6">
                      <div className="h-px bg-[#2A2F45] mb-4"></div>
                      <p className="text-gray-400 font-rajdhani">{faq.a}</p>
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="bg-gradient-to-r from-[#00fff510] to-[#7000ff10] border border-[#2A2F45] rounded-3xl p-12 text-center"
          >
            <Mail className="w-16 h-16 text-[#00fff5] mx-auto mb-6" />
            <h2 className="text-4xl md:text-5xl font-rajdhani font-bold text-white mb-4">
              Need <span className="text-[#00fff5]">Help</span>?
            </h2>
            <p className="text-xl text-gray-400 font-rajdhani mb-8 max-w-2xl mx-auto">
              Our support team is here to assist with any refund-related questions or issues.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="mailto:support@lastzoneindia.com?subject=Refund%20Request&body=Please%20provide%20your%20transaction%20details%20and%20reason%20for%20refund:"
                className="inline-flex items-center gap-2 bg-gradient-to-r from-[#00fff5] to-[#7000ff] px-8 py-4 rounded-xl text-white font-rajdhani font-bold hover:opacity-90 transition"
              >
                <Mail className="w-5 h-5" />
                Request Refund
              </a>
              
              <Link to="/faq">
                <button className="bg-[#1E2338] border border-[#2A2F45] px-8 py-4 rounded-xl text-white font-rajdhani font-bold hover:bg-[#2A2F45] transition flex items-center gap-2">
                  <HelpCircle className="w-5 h-5" />
                  Visit FAQ
                </button>
              </Link>
            </div>

            <div className="mt-8 flex items-center justify-center gap-2 text-sm text-gray-500">
              <ThumbsUp className="w-4 h-4" />
              <span>98% of refund requests resolved within 48 hours</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer Note */}
      <section className="py-8 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-gray-500 font-rajdhani text-sm">
            By using TheArenaX, you agree to this Refund & Cancellation Policy. 
            We reserve the right to update this policy as needed. Last updated: March 2026.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Refund;